import { useFlags } from "launchdarkly-react-client-sdk"
import { useCallback, useMemo } from "react"
import { matchPath } from "react-router"
import { FULL_ACCESS_ROUTES, ROUTES } from "../utils/constants"
import { objectKeys } from "../utils/helpers"
import { useShowAlertCuration } from "./useFeatureFlags"
import { useRoles } from "./useRoles"

type RouteKey = keyof typeof ROUTES

/**
 * Centralizes route access logic combining feature flags and role checks.
 *
 * Feature flags control whether a feature is enabled at all (e.g. for phased rollout),
 * while role checks ensure the user is authorized to see the content.
 * Both must pass for access to be granted.
 */
export function useRouteAccess() {
  const { hasRole, hasPHCAccessRole } = useRoles()
  const {
    showOahu,
    showBioMesh,
    allowNonBetaRoutes,
    showAgenticEpi,
    showLocationsPage,
  } = useFlags<{
    showAgenticEpi?: boolean
    showOahu?: boolean
    showBioMesh?: boolean
    allowNonBetaRoutes?: boolean
    showLocationsPage?: boolean
  }>()
  const showAlertCuration = useShowAlertCuration()

  /** Per-route access rules: feature flag + optional role check. */
  const accessRules = useMemo<Partial<Record<RouteKey, boolean>>>(() => {
    const hasPHCRole = hasPHCAccessRole()
    return {
      ASSETS: !!showLocationsPage,
      WASTEWATER_OAHU: !!showOahu && hasRole("wastewater-analyst"),
      BIO_MESH: !!showBioMesh && hasRole("biomesh-analyst"),
      COP: !!showBioMesh && hasRole("biomesh-analyst"),
      BIOTHREAT_SIGNALS: showAlertCuration && hasPHCRole,
      BIOTHREAT_SIGNAL_DETAIL: showAlertCuration && hasPHCRole,
      WINGMAN: !!showAgenticEpi && hasPHCRole,
      WINGMAN_CHAT: !!showAgenticEpi && hasPHCRole,
    } satisfies Record<string, boolean>
  }, [
    showOahu,
    showBioMesh,
    showAlertCuration,
    showAgenticEpi,
    showLocationsPage,
    hasPHCAccessRole,
    hasRole,
  ])

  const resolveAccess = useCallback(
    (routeKey: RouteKey): boolean => {
      const rule = accessRules[routeKey]
      if (rule !== undefined) {
        return rule
      }
      if (FULL_ACCESS_ROUTES.includes(ROUTES[routeKey])) {
        return !!allowNonBetaRoutes
      }
      // Core routes (PULSE, PREFERENCES, FAQ, etc.) require no extra gating.
      // Unprotected routes (SIGN_IN, etc.) are handled by RequireAuth before
      // this hook is consulted.
      return true
    },
    [accessRules, allowNonBetaRoutes]
  )

  /** Check access by pathname (used in route guards). */
  const canAccessPath = useCallback(
    (pathname: string): boolean => {
      const matchedKey = objectKeys(ROUTES).find(key =>
        matchPath(ROUTES[key], pathname)
      )
      return matchedKey ? resolveAccess(matchedKey) : true
    },
    [resolveAccess]
  )

  return { canAccessRoute: resolveAccess, canAccessPath }
}
